export interface IDOMSnapshotConfig {
  includeStyles: boolean;
  includeInlineStyles: boolean;
  includeComputedStyles: boolean;
  maxDepth: number;
  excludeSelectors: string[];
  virtualizeOffscreen: boolean;
  captureViewportOnly: boolean;
  compressWhitespace: boolean;
  removeComments: boolean;
  simplifyDataAttributes: boolean;
}

export interface IDOMSnapshot {
  id: string;
  timestamp: number;
  url: string;
  viewport: {
    width: number;
    height: number;
    scrollX: number;
    scrollY: number;
  };
  html: string;
  styles?: string;
  metadata: {
    nodeCount: number;
    originalSize: number;
    compressedSize: number;
    compressionRatio: number;
  };
}

export interface IDOMDiff {
  added: string[];
  removed: string[];
  modified: string[];
  moved: string[];
  attributeChanges: Array<{
    selector: string;
    attribute: string;
    oldValue: string | null;
    newValue: string | null;
  }>;
}

/**
 * DOMSnapshotOptimizer - DOM 스냅샷을 최적화하여 캡처하고 관리하는 클래스
 */
export class DOMSnapshotOptimizer {
  private _config: IDOMSnapshotConfig;
  private _lastSnapshot: IDOMSnapshot | null = null;
  private _observer: MutationObserver | null = null;
  private _pendingChanges: Set<Element> = new Set();

  constructor(config: Partial<IDOMSnapshotConfig> = {}) {
    this._config = {
      includeStyles: true,
      includeInlineStyles: true,
      includeComputedStyles: false, // 성능상 기본 비활성화
      maxDepth: 10,
      excludeSelectors: [
        'script',
        'style',
        '.debug-time-machine',
        '[data-debug-ignore]',
        'noscript',
        'template',
      ],
      virtualizeOffscreen: true,
      captureViewportOnly: false,
      compressWhitespace: true,
      removeComments: true,
      simplifyDataAttributes: true,
      ...config,
    };
  }

  /**
   * 현재 DOM의 전체 스냅샷을 생성합니다
   */
  public createFullSnapshot(): IDOMSnapshot {
    const startTime = performance.now();
    
    const rootElement = this._config.captureViewportOnly 
      ? this._getViewportElements() 
      : document.documentElement;

    const processedHTML = this._processElement(rootElement, 0);
    const styles = this._config.includeStyles ? this._extractStyles() : undefined;
    
    const originalHTML = rootElement.outerHTML;
    const originalSize = new Blob([originalHTML]).size;
    const compressedSize = new Blob([processedHTML]).size;
    
    const snapshot: IDOMSnapshot = {
      id: this._generateId(),
      timestamp: Date.now(),
      url: window.location.href,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      },
      html: processedHTML,
      styles,
      metadata: {
        nodeCount: this._countNodes(rootElement),
        originalSize,
        compressedSize,
        compressionRatio: compressedSize / originalSize,
      },
    };

    this._lastSnapshot = snapshot;
    
    const processingTime = performance.now() - startTime;
    console.log(`DOM snapshot created in ${processingTime.toFixed(2)}ms`, {
      nodeCount: snapshot.metadata.nodeCount,
      compression: `${(snapshot.metadata.compressionRatio * 100).toFixed(1)}%`,
      size: `${(compressedSize / 1024).toFixed(1)}KB`,
    });

    return snapshot;
  }

  /**
   * 뷰포트 내의 요소들만 캡처합니다
   */
  public captureViewportOnly(): IDOMSnapshot {
    const viewportElements = this._getViewportElements();
    
    // 임시 컨테이너에 뷰포트 요소들 복사
    const container = document.createElement('div');
    container.innerHTML = viewportElements.outerHTML;
    
    const processedHTML = this._processElement(container.firstElementChild as Element, 0);
    
    const snapshot: IDOMSnapshot = {
      id: this._generateId(),
      timestamp: Date.now(),
      url: window.location.href,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      },
      html: processedHTML,
      styles: this._config.includeStyles ? this._extractViewportStyles() : undefined,
      metadata: {
        nodeCount: this._countNodes(container),
        originalSize: 0,
        compressedSize: new Blob([processedHTML]).size,
        compressionRatio: 1,
      },
    };

    return snapshot;
  }

  /**
   * 증분 스냅샷을 생성합니다 (변경된 부분만)
   */
  public createIncrementalSnapshot(): IDOMDiff | null {
    if (!this._lastSnapshot) {
      // 첫 번째 스냅샷이면 전체 스냅샷 생성
      this.createFullSnapshot();
      return null;
    }

    // MutationObserver를 통해 수집된 변경사항 분석
    const changes = this._analyzePendingChanges();
    this._pendingChanges.clear();

    return changes;
  }

  /**
   * DOM 변경사항 모니터링을 시작합니다
   */
  public startMonitoring(): void {
    if (this._observer) {
      this._observer.disconnect();
    }

    this._observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // 제외할 요소인지 확인
        if (this._shouldExcludeElement(mutation.target as Element)) {
          return;
        }

        // 변경된 요소를 추적
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this._pendingChanges.add(node as Element);
            }
          });
          
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this._pendingChanges.add(node as Element);
            }
          });
        } else if (mutation.type === 'attributes') {
          this._pendingChanges.add(mutation.target as Element);
        }
      });
    });

    this._observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      characterData: true,
      characterDataOldValue: true,
    });

    console.log('DOM monitoring started');
  }

  /**
   * DOM 변경사항 모니터링을 중단합니다
   */
  public stopMonitoring(): void {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    this._pendingChanges.clear();
    console.log('DOM monitoring stopped');
  }

  /**
   * DOM 트리를 압축합니다
   */
  public compressDOMTree(html: string): string {
    let compressed = html;

    if (this._config.removeComments) {
      compressed = compressed.replace(/<!--[\s\S]*?-->/g, '');
    }

    if (this._config.compressWhitespace) {
      compressed = compressed.replace(/\s+/g, ' ');
      compressed = compressed.replace(/>\s+</g, '><');
    }

    if (this._config.simplifyDataAttributes) {
      // data-* 속성 중 불필요한 것들 제거
      compressed = compressed.replace(/\s+data-react-[^=]*="[^"]*"/g, '');
      compressed = compressed.replace(/\s+data-testid="[^"]*"/g, '');
    }

    return compressed.trim();
  }

  /**
   * 스냅샷 복원
   */
  public restoreSnapshot(snapshot: IDOMSnapshot): void {
    try {
      // 스타일 복원
      if (snapshot.styles) {
        this._restoreStyles(snapshot.styles);
      }

      // DOM 복원 (주의: 이는 데모 목적이며 실제로는 iframe 등에서 수행)
      console.log('Snapshot restoration would apply:', {
        timestamp: new Date(snapshot.timestamp).toISOString(),
        nodeCount: snapshot.metadata.nodeCount,
        url: snapshot.url,
      });

      // 실제 복원은 위험하므로 로그만 출력
      // document.documentElement.innerHTML = snapshot.html;
    } catch (error) {
      console.error('Failed to restore DOM snapshot:', error);
    }
  }

  /**
   * 리소스 정리
   */
  public destroy(): void {
    this.stopMonitoring();
    this._lastSnapshot = null;
    this._pendingChanges.clear();
  }

  /**
   * 요소를 처리하여 최적화된 HTML을 생성합니다
   */
  private _processElement(element: Element, depth: number): string {
    if (!element || depth > this._config.maxDepth) {
      return '';
    }

    if (this._shouldExcludeElement(element)) {
      return '';
    }

    // 오프스크린 요소 가상화
    if (this._config.virtualizeOffscreen && this._isOffscreen(element)) {
      return this._createVirtualPlaceholder(element);
    }

    const cloned = element.cloneNode(false) as Element;
    
    // 속성 정리
    this._cleanupAttributes(cloned);
    
    // 스타일 처리
    if (this._config.includeComputedStyles) {
      this._addComputedStyles(cloned, element);
    }

    let innerHTML = '';
    
    // 자식 요소들 처리
    for (const child of Array.from(element.children)) {
      innerHTML += this._processElement(child, depth + 1);
    }

    // 텍스트 노드 처리
    const textContent = this._extractTextContent(element);
    if (textContent) {
      innerHTML = textContent + innerHTML;
    }

    cloned.innerHTML = innerHTML;
    
    return this.compressDOMTree(cloned.outerHTML);
  }

  /**
   * 요소를 제외해야 하는지 확인합니다
   */
  private _shouldExcludeElement(element: Element): boolean {
    if (!element || !element.matches) {
      return true;
    }

    return this._config.excludeSelectors.some(selector => {
      try {
        return element.matches(selector);
      } catch {
        return false;
      }
    });
  }

  /**
   * 요소가 오프스크린인지 확인합니다
   */
  private _isOffscreen(element: Element): boolean {
    const rect = element.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    return (
      rect.bottom < 0 ||
      rect.right < 0 ||
      rect.left > viewport.width ||
      rect.top > viewport.height
    );
  }

  /**
   * 가상 플레이스홀더를 생성합니다
   */
  private _createVirtualPlaceholder(element: Element): string {
    const rect = element.getBoundingClientRect();
    return `<div data-virtual-element="${element.tagName}" style="width:${rect.width}px;height:${rect.height}px;background:#f0f0f0;border:1px dashed #ccc;"></div>`;
  }

  /**
   * 속성을 정리합니다
   */
  private _cleanupAttributes(element: Element): void {
    const attributesToRemove = [
      'data-reactroot',
      'data-react-checksum',
    ];

    attributesToRemove.forEach(attr => {
      element.removeAttribute(attr);
    });

    // 이벤트 핸들러 제거
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('on')) {
        element.removeAttribute(attr.name);
      }
    });
  }

  /**
   * 계산된 스타일을 추가합니다
   */
  private _addComputedStyles(target: Element, source: Element): void {
    if (!this._config.includeComputedStyles) {
      return;
    }

    const computedStyle = window.getComputedStyle(source);
    const importantStyles = [
      'display',
      'position',
      'width',
      'height',
      'margin',
      'padding',
      'border',
      'background',
      'color',
      'font',
    ];

    const styleString = importantStyles
      .map(prop => `${prop}:${computedStyle.getPropertyValue(prop)}`)
      .join(';');

    target.setAttribute('data-computed-style', styleString);
  }

  /**
   * 텍스트 내용을 추출합니다
   */
  private _extractTextContent(element: Element): string {
    const textNodes = Array.from(element.childNodes)
      .filter(node => node.nodeType === Node.TEXT_NODE)
      .map(node => node.textContent?.trim())
      .filter(text => text && text.length > 0);

    return textNodes.join(' ');
  }

  /**
   * 뷰포트 내 요소들을 가져옵니다
   */
  private _getViewportElements(): Element {
    // 간단한 구현: body 전체를 반환
    // 실제로는 뷰포트와 교차하는 요소들만 필터링
    return document.body;
  }

  /**
   * 스타일을 추출합니다
   */
  private _extractStyles(): string {
    const styleSheets = Array.from(document.styleSheets);
    let styles = '';

    styleSheets.forEach(sheet => {
      try {
        const rules = Array.from(sheet.cssRules || []);
        rules.forEach(rule => {
          styles += rule.cssText + '\n';
        });
      } catch (error) {
        // CORS 등으로 접근할 수 없는 스타일시트는 무시
        console.warn('Cannot access stylesheet:', sheet.href, error);
      }
    });

    return styles;
  }

  /**
   * 뷰포트 관련 스타일만 추출합니다
   */
  private _extractViewportStyles(): string {
    // 뷰포트 내 요소들에 적용된 스타일만 추출하는 최적화된 로직
    return this._extractStyles(); // 간단한 구현
  }

  /**
   * 노드 수를 계산합니다
   */
  private _countNodes(element: Element): number {
    let count = 1;
    for (const child of Array.from(element.children)) {
      count += this._countNodes(child);
    }
    return count;
  }

  /**
   * 대기 중인 변경사항을 분석합니다
   */
  private _analyzePendingChanges(): IDOMDiff {
    // 간단한 구현 - 실제로는 더 정교한 diff 알고리즘 필요
    const changes: IDOMDiff = {
      added: [],
      removed: [],
      modified: [],
      moved: [],
      attributeChanges: [],
    };

    this._pendingChanges.forEach(element => {
      // 요소의 변경 유형 분석
      if (element.parentNode) {
        changes.modified.push(this._getElementSelector(element));
      } else {
        changes.removed.push(this._getElementSelector(element));
      }
    });

    return changes;
  }

  /**
   * 요소의 CSS 셀렉터를 생성합니다
   */
  private _getElementSelector(element: Element): string {
    if (element.id) {
      return `#${element.id}`;
    }

    const path = [];
    let current = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let selector = current.tagName.toLowerCase();
      
      if (current.className) {
        const classes = current.className.split(' ').filter(c => c.trim());
        if (classes.length > 0) {
          selector += '.' + classes.slice(0, 2).join('.');
        }
      }

      path.unshift(selector);
      current = current.parentElement as Element;

      if (path.length >= 5 || !current) break;
    }

    return path.join(' > ');
  }

  /**
   * 스타일을 복원합니다
   */
  private _restoreStyles(styles: string): void {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    styleElement.setAttribute('data-debug-time-machine', 'true');
    document.head.appendChild(styleElement);
  }

  /**
   * 고유 ID를 생성합니다
   */
  private _generateId(): string {
    return `dom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
